// src/utils/coverImage.js

/**
 * Prepara a imagem escolhida pelo usuário para virar capa de texto.
 *
 * O redimensionamento acontece AQUI, no navegador, e não no servidor. Três
 * motivos, nesta ordem:
 *
 *   1. o backend não tem biblioteca de imagem nenhuma (conferido no .csproj:
 *      AngleSharp, PdfPig, Mammoth, DinkToPdf, Xceed — nenhuma redimensiona), e
 *      acrescentar uma só para refazer o que o navegador já sabe fazer seria
 *      dependência nova numa aplicação que não precisa dela;
 *   2. a foto de um celular tem 4 a 8 MB; a capa gravada tem algumas dezenas de
 *      KB. Cortar antes de subir poupa a rede do usuário e o disco do servidor,
 *      em vez de transferir megabytes para descartar 99% deles;
 *   3. o recorte precisa ser combinado com o layout do card, que é 3:4 fixo —
 *      decisão de apresentação, que mora no front.
 *
 * O servidor NÃO confia nisto: `TextsController` valida tipo e tamanho de novo.
 * Cliente é conveniência, não autoridade.
 *
 * ⚠️ A proporção aqui e o `aspect-ratio` do `.card-cover` (TextCardsForm.vue)
 * são o mesmo número escrito em dois lugares. Se divergirem, a capa gravada
 * aparece esticada ou com faixa vazia — sem erro nenhum.
 */

/** Proporção de capa de livro, a mesma do `.card-cover`. */
export const COVER_ASPECT = 3 / 4;

/**
 * Largura gravada. 400 cobre os 116px do card em tela de 3x de densidade e
 * ainda serve uma exibição maior (o card de detalhes, por exemplo). Guardar
 * mais que isso é peso que nunca vira pixel na tela.
 */
export const COVER_WIDTH = 400;
export const COVER_HEIGHT = Math.round(COVER_WIDTH / COVER_ASPECT); // 533

/** Teto do arquivo ESCOLHIDO — não do gravado, que sai muito menor. */
export const MAX_SOURCE_BYTES = 12 * 1024 * 1024;

export const ACCEPTED_SOURCE_TYPES = ["image/jpeg", "image/png", "image/webp"];

/**
 * Converte um File de imagem na capa final.
 *
 * @param {File} file imagem escolhida pelo usuário
 * @returns {Promise<{ blob: Blob, previewUrl: string, type: string }>}
 *   `previewUrl` é um object URL — quem chama precisa revogá-lo
 *   (URL.revokeObjectURL) ao trocar de imagem ou desmontar, senão o blob fica
 *   retido enquanto a aba viver.
 * @throws {Error} com mensagem pronta para exibição quando o arquivo é recusado
 */
export async function buildCoverFromFile(file) {
  if (!file) throw new Error("Nenhuma imagem selecionada.");

  if (!ACCEPTED_SOURCE_TYPES.includes(file.type)) {
    throw new Error("A capa deve ser uma imagem JPEG, PNG ou WebP.");
  }

  if (file.size > MAX_SOURCE_BYTES) {
    throw new Error("A imagem escolhida não pode exceder 12 MB.");
  }

  const bitmap = await decode(file);

  try {
    const canvas = document.createElement("canvas");
    canvas.width = COVER_WIDTH;
    canvas.height = COVER_HEIGHT;

    const ctx = canvas.getContext("2d");
    // Fundo branco: PNG com transparência viraria preto ao ser convertido para
    // um formato sem alfa, e a capa deve continuar parecendo papel.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, COVER_WIDTH, COVER_HEIGHT);
    // O padrão do canvas já é bom para redução; explícito para não depender de
    // default de navegador numa operação em que borrar é visível.
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    const crop = coverCrop(bitmap.width, bitmap.height);
    ctx.drawImage(
      bitmap,
      crop.x,
      crop.y,
      crop.width,
      crop.height,
      0,
      0,
      COVER_WIDTH,
      COVER_HEIGHT
    );

    const blob = await encode(canvas);
    return {
      blob,
      type: blob.type,
      previewUrl: URL.createObjectURL(blob),
    };
  } finally {
    // ImageBitmap segura memória de imagem descomprimida — uma foto de 12 MP
    // são ~48 MB aqui. Fechar não é zelo.
    if (typeof bitmap.close === "function") bitmap.close();
  }
}

/**
 * Recorte "cover": preenche o quadro inteiro e descarta o excesso do lado mais
 * longo, sempre pelo CENTRO. É o oposto de "contain", que caberia tudo e
 * deixaria faixas vazias — e faixa vazia numa grade de capas lê como imagem
 * quebrada.
 */
function coverCrop(sourceWidth, sourceHeight) {
  const sourceAspect = sourceWidth / sourceHeight;

  if (sourceAspect > COVER_ASPECT) {
    // Mais larga que o quadro: corta nas laterais.
    const width = sourceHeight * COVER_ASPECT;
    return {
      x: (sourceWidth - width) / 2,
      y: 0,
      width,
      height: sourceHeight,
    };
  }

  // Mais alta que o quadro: corta em cima e embaixo.
  const height = sourceWidth / COVER_ASPECT;
  return {
    x: 0,
    y: (sourceHeight - height) / 2,
    width: sourceWidth,
    height,
  };
}

/**
 * `createImageBitmap` é o caminho rápido (decodifica fora da thread principal),
 * mas nem todo navegador o expõe para Blob. O <img> é a rede.
 */
async function decode(file) {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file);
    } catch {
      // formato que o navegador não decodifica por esse caminho — tenta o <img>
    }
  }

  const url = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () =>
        reject(new Error("Não foi possível ler a imagem escolhida."));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * WebP com qualidade 0.82 — a faixa em que a perda deixa de ser visível numa
 * capa e o arquivo ainda cai para dezenas de KB. O PNG é a rede para navegador
 * sem WebP: `toBlob` com tipo não suportado devolve PNG silenciosamente, então
 * o tipo real é lido do próprio blob e é ele que o backend valida.
 */
function encode(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Não foi possível processar a imagem."));
      },
      "image/webp",
      0.82
    );
  });
}
